import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import QueueIcon from '@mui/icons-material/Queue';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SuperAdminLayout from './SuperAdminLayout';
import SuperAdminService, { Company } from '../../services/SuperAdminService';
import {
  JobRow,
  JobSummary,
  JobView,
  cancelJob,
  getJobSummary,
  getJobs,
  retryJob,
  retryJobsBulk,
} from '../../services/JobQueueService';

// ============================================================================
// SÜPER ADMİN › İŞ KUYRUĞU
// Ortak iş kuyruğunun (GtsJobs) tüm veritabanlarındaki durumu. Ölü işler deneme
// hakkını bitirmiş, elle bakılması gereken işlerdir: sebep giderilince "Tekrar dene"
// deneme sayısını sıfırlar ve işi hemen kuyruğa koyar.
// ============================================================================

const REFRESH_MS = 15000;

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: 'center',
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
}));

const STATUS_CHIP: Record<number, { label: string; bg: string; fg: string }> = {
  0: { label: 'Bekliyor', bg: '#fff3e0', fg: '#8a4b00' },
  1: { label: 'Çalışıyor', bg: '#e3f2fd', fg: '#0d47a1' },
  2: { label: 'Tamamlandı', bg: '#e8f5e9', fg: '#1b5e20' },
  3: { label: 'Ölü', bg: '#fdecea', fg: '#b71c1c' },
  4: { label: 'İptal', bg: '#eceff1', fg: '#37474f' },
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDateLong = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR');
};

const fmtAge = (seconds?: number | null) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds} sn`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} dk`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} sa`;
  return `${Math.floor(seconds / 86400)} gün`;
};

const shortRef = (ref?: string | null) => {
  if (!ref) return '';
  const [kind, value] = ref.includes(':') ? [ref.slice(0, ref.indexOf(':')), ref.slice(ref.indexOf(':') + 1)] : ['', ref];
  const label = kind === 'PavoRequest' ? 'Pavo istek' : kind;
  const v = value.length > 14 ? `${value.slice(0, 4)}…${value.slice(-3)}` : value;
  return label ? `${label} ${v}` : v;
};

const rowKey = (r: JobRow) => `${r.db}:${r.id}`;

const SuperAdminJobQueue: React.FC = () => {
  const [view, setView] = useState<JobView>('dead');
  const [summary, setSummary] = useState<JobSummary | null>(null);
  const [rows, setRows] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [dbFilter, setDbFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [reference, setReference] = useState('');
  const [referenceApplied, setReferenceApplied] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<JobRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; text: string; run: () => Promise<void> } | null>(null);

  const loadSeq = useRef(0);

  // Şirket adları (filtre için)
  useEffect(() => {
    SuperAdminService.getInstance()
      .getAllCompanies()
      .then((list) => setCompanies([...list].sort((a, b) => a.name.localeCompare(b.name, 'tr'))))
      .catch(() => setCompanies([]));
  }, []);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        getJobSummary(),
        getJobs({
          view,
          db: dbFilter || undefined,
          jobType: typeFilter || undefined,
          companyId: company?.id,
          reference: referenceApplied || undefined,
          take: 200,
        }),
      ]);
      if (seq !== loadSeq.current) return;
      setSummary(s);
      setRows(list.jobs);
      setError(null);
      setLastRefresh(new Date());
      // Listede artık olmayan seçimleri bırak
      setSelected((prev) => {
        const keys = new Set(list.jobs.map(rowKey));
        return new Set([...prev].filter((k) => keys.has(k)));
      });
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e instanceof Error ? e.message : 'Yüklenemedi');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [view, dbFilter, typeFilter, company, referenceApplied]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Referans aramasında her tuşta istek atma
  useEffect(() => {
    const t = setTimeout(() => setReferenceApplied(reference.trim()), 500);
    return () => clearTimeout(t);
  }, [reference]);

  const jobTypeOptions = useMemo(() => Object.entries(summary?.jobTypes ?? {}), [summary]);
  const dbOptions = useMemo(() => summary?.databases ?? [], [summary]);
  const missingDbs = dbOptions.filter((d) => !d.available).map((d) => d.database);

  const total = summary?.total;
  const stats = [
    { label: 'Ölü iş', value: total ? String(total.dead) : '—', color: '#d32f2f', strong: (total?.dead ?? 0) > 0 },
    { label: 'Bekleyen', value: total ? String(total.pending) : '—', color: '#ef6c00' },
    { label: 'Çalışan', value: total ? String(total.running) : '—', color: '#1976d2' },
    { label: 'En eski bekleyen', value: fmtAge(total?.oldestPendingSeconds), color: '#7b1fa2' },
    {
      label: 'Son 24 saatte tamamlanan',
      value: total ? total.succeededLast24h.toLocaleString('tr-TR') : '—',
      color: '#2e7d32',
    },
  ];

  const canAct = (r: JobRow) => r.status === 0 || r.status === 3 || r.status === 4;
  const canCancel = (r: JobRow) => r.status === 0 || r.status === 3;
  const selectableRows = rows.filter(canAct);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(rowKey(r)));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableRows.map(rowKey)));
  };

  const toggleOne = (r: JobRow) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = rowKey(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const doRetry = async (r: JobRow) => {
    setBusy(rowKey(r));
    try {
      await retryJob(r.db, r.id);
      setToast({ msg: `İş #${r.id} yeniden kuyruğa alındı.`, severity: 'success' });
      setDetail(null);
      await load();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Tekrar denenemedi', severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async (r: JobRow) => {
    setBusy(rowKey(r));
    try {
      await cancelJob(r.db, r.id);
      setToast({ msg: `İş #${r.id} iptal edildi.`, severity: 'info' });
      setDetail(null);
      await load();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'İptal edilemedi', severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const askCancel = (r: JobRow) =>
    setConfirm({
      title: 'İş iptal edilsin mi?',
      text: `${r.jobTypeName} (#${r.id}) bir daha denenmeyecek. Bu işin yapacağı şeyi (ör. masa kapatma, fatura) gerekiyorsa elle yapmanız gerekir.`,
      run: () => doCancel(r),
    });

  const askBulkRetry = () => {
    const items = rows.filter((r) => selected.has(rowKey(r))).map((r) => ({ db: r.db, id: r.id }));
    if (items.length === 0) return;
    setConfirm({
      title: `${items.length} iş tekrar denensin mi?`,
      text: 'Seçilen işlerin deneme sayısı sıfırlanır ve hemen kuyruğa alınır. Önce hatanın sebebini giderdiğinizden emin olun.',
      run: async () => {
        setBusy('bulk');
        try {
          const res = await retryJobsBulk(items);
          setToast({
            msg: `${res.retried} iş kuyruğa alındı${res.skipped ? `, ${res.skipped} iş atlandı (çalışıyor ya da bitmiş)` : ''}.`,
            severity: res.skipped ? 'info' : 'success',
          });
          setSelected(new Set());
          await load();
        } catch (e) {
          setToast({ msg: e instanceof Error ? e.message : 'Toplu tekrar başarısız', severity: 'error' });
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const tabCount = (v: JobView) => {
    if (!total) return '';
    if (v === 'dead') return ` (${total.dead})`;
    if (v === 'active') return ` (${total.pending + total.running})`;
    return '';
  };

  const emptyText: Record<JobView, string> = {
    dead: 'Ölü iş yok. Her şey yolunda.',
    active: 'Bekleyen ya da çalışan iş yok.',
    done: 'Son 1 gün içinde tamamlanan iş yok (tamamlanan işler 1 gün sonra silinir).',
    cancelled: 'İptal edilmiş iş yok.',
  };

  return (
    <SuperAdminLayout title="İş Kuyruğu" icon={<QueueIcon />} maxWidth={false}>
      <Box sx={{ p: { xs: 0, md: 1 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 420px' }}>
            Satış sonrası arka plan işleri (Pavo masa kapatma, paket faturası…). Ölü işler, deneme hakkını bitirmiş ve elle
            bakılması gereken işlerdir.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lastRefresh ? `Son güncelleme ${lastRefresh.toLocaleTimeString('tr-TR')} · 15 sn'de bir yenilenir` : ''}
          </Typography>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Yenile
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {missingDbs.length > 0 && (
          <Alert severity="warning">
            Şu veritabanlarında kuyruk tablosu kurulu değil: {missingDbs.join(', ')}. Bu şirketlerde işler eski yöntemle
            çalışıyor; _SQL_MIGRATIONS/JOBS_GtsJobs.sql çalıştırılmalı.
          </Alert>
        )}

        <Grid container spacing={2}>
          {stats.map((s) => (
            <Grid item xs={6} sm={4} md key={s.label}>
              <StatCard elevation={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: '6px', bgcolor: s.color }} aria-hidden />
                <Typography variant="h5" sx={{ fontWeight: 700, color: s.strong ? '#b71c1c' : 'text.primary' }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={view}
            onChange={(_, v: JobView) => {
              setView(v);
              setSelected(new Set());
            }}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="dead" label={`Ölü işler${tabCount('dead')}`} />
            <Tab value="active" label={`Bekleyen / Çalışan${tabCount('active')}`} />
            <Tab value="done" label="Son tamamlananlar" />
            <Tab value="cancelled" label="İptal edilenler" />
          </Tabs>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, p: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="jq-db">Veritabanı</InputLabel>
              <Select labelId="jq-db" label="Veritabanı" value={dbFilter} onChange={(e) => setDbFilter(e.target.value)}>
                <MenuItem value="">Tümü ({dbOptions.length})</MenuItem>
                {dbOptions.map((d) => (
                  <MenuItem key={d.database} value={d.database}>
                    {d.database}
                    {d.available ? '' : ' (kurulu değil)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel id="jq-type">İş türü</InputLabel>
              <Select labelId="jq-type" label="İş türü" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="">Tümü</MenuItem>
                {jobTypeOptions.map(([key, name]) => (
                  <MenuItem key={key} value={key}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              size="small"
              sx={{ flex: '1 1 240px', minWidth: 220 }}
              options={companies}
              value={company}
              onChange={(_, v) => setCompany(v)}
              getOptionLabel={(c) => c.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Şirket" placeholder="Şirket adı yazın" />}
            />
            <TextField
              size="small"
              label="Referans"
              placeholder="Pavo istek no ya da iş no"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              sx={{ flex: '1 1 220px', minWidth: 200 }}
            />
            {view !== 'done' && (
              <Button
                variant="contained"
                disabled={selected.size === 0 || busy === 'bulk'}
                onClick={askBulkRetry}
              >
                Seçilenleri tekrar dene ({selected.size})
              </Button>
            )}
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell padding="checkbox">
                    {view !== 'done' && (
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selected.size > 0 && !allSelected}
                        onChange={toggleAll}
                        disabled={selectableRows.length === 0}
                        inputProps={{ 'aria-label': 'Tümünü seç' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İş</TableCell>
                  <TableCell>Şirket / Şube</TableCell>
                  <TableCell>Deneme</TableCell>
                  <TableCell>{view === 'done' ? 'Sonuç' : 'Son hata'}</TableCell>
                  <TableCell>{view === 'active' ? 'Sıradaki deneme' : 'Oluşma'}</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {emptyText[view]}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => {
                  const chip = STATUS_CHIP[r.status] ?? STATUS_CHIP[0];
                  const k = rowKey(r);
                  return (
                    <TableRow key={k} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell padding="checkbox">
                        {view !== 'done' && (
                          <Checkbox
                            checked={selected.has(k)}
                            onChange={() => toggleOne(r)}
                            disabled={!canAct(r)}
                            inputProps={{ 'aria-label': `İş ${r.id} seç` }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 500 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {r.jobTypeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{r.id}
                          {r.reference ? ` · ${shortRef(r.reference)}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.companyName || (r.companyId ? 'Bilinmeyen şirket' : '—')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.branchName || (r.branchId ? 'Şube' : 'Şube yok')} · {r.db}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.attempts} / {r.maxAttempts}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 380 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: view === 'done' ? 'text.primary' : '#8a1c1c',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {r.lastError || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {view === 'active' ? fmtDate(r.runAfter) : fmtDate(r.createdAt)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" onClick={() => setDetail(r)}>
                          Detay
                        </Button>
                        {canAct(r) && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                            disabled={busy === k}
                            onClick={() => doRetry(r)}
                          >
                            Tekrar dene
                          </Button>
                        )}
                        {canCancel(r) && (
                          <Button
                            size="small"
                            color="inherit"
                            sx={{ ml: 0.5, color: 'text.secondary' }}
                            disabled={busy === k}
                            onClick={() => askCancel(r)}
                          >
                            İptal
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {rows.length} iş gösteriliyor{rows.length >= 200 ? ' (en fazla 200; filtre ile daraltın)' : ''}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* İş detayı */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
                  {detail.jobTypeName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  İş #{detail.id} · {detail.jobType} · {detail.db} veritabanı
                </Typography>
              </Box>
              <Chip
                label={(STATUS_CHIP[detail.status] ?? STATUS_CHIP[0]).label}
                sx={{ bgcolor: (STATUS_CHIP[detail.status] ?? STATUS_CHIP[0]).bg, color: (STATUS_CHIP[detail.status] ?? STATUS_CHIP[0]).fg }}
              />
              <Tooltip title="Kapat">
                <IconButton aria-label="Kapat" onClick={() => setDetail(null)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                {[
                  ['Şirket', detail.companyName || detail.companyId || '—'],
                  ['Şube', detail.branchName || (detail.branchId ? 'Şube' : '—')],
                  ['Referans', detail.reference || '—'],
                  ['İşi yazan API', detail.source || '—'],
                  ['Oluşma', fmtDateLong(detail.createdAt)],
                  ['Son güncelleme', `${fmtDateLong(detail.updatedAt)} (${detail.attempts} / ${detail.maxAttempts})`],
                  ...(detail.status === 0 ? [['Sıradaki deneme', fmtDateLong(detail.runAfter)]] : []),
                  ...(detail.lockedBy ? [['Çalıştıran kopya', detail.lockedBy]] : []),
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {detail.status === 2 ? 'Sonuç' : 'Son hata'}
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: detail.status === 2 ? '#e8f5e9' : '#fdecea',
                    color: detail.status === 2 ? '#1b5e20' : '#8a1c1c',
                    fontSize: 14,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {detail.lastError || '—'}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  İş içeriği
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#f5f7fa',
                    border: '1px solid #e4e7eb',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {(() => {
                    if (!detail.payloadJson) return '—';
                    try {
                      return JSON.stringify(JSON.parse(detail.payloadJson), null, 2);
                    } catch {
                      return detail.payloadJson;
                    }
                  })()}
                </Box>
              </Box>
              {canAct(detail) && (
                <Alert severity="info">
                  Tekrar denemeden önce hatanın sebebini giderin (ör. kasa hesabını tanımlayın). Tekrar dene, deneme
                  sayısını sıfırlar ve işi hemen kuyruğa koyar. Paket faturası tekrar anahtarıyla gittiği için ikinci
                  fatura oluşmaz.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              {canCancel(detail) && (
                <Button color="error" onClick={() => askCancel(detail)} disabled={busy === rowKey(detail)}>
                  İptal et
                </Button>
              )}
              <Button color="inherit" onClick={() => setDetail(null)}>
                Kapat
              </Button>
              {canAct(detail) && (
                <Button variant="contained" onClick={() => doRetry(detail)} disabled={busy === rowKey(detail)}>
                  Tekrar dene
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Onay */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm?.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirm(null)}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const c = confirm;
              setConfirm(null);
              if (c) await c.run();
            }}
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        </Snackbar>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminJobQueue;
